import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Send, Eye, Building2, Users } from 'lucide-react';

export default function PulseBreakdownModal({ pulse, gyms, open, onClose, selectedDate }) {
  const navigate = useNavigate();
  const [sendingReminder, setSendingReminder] = useState({});

  // Filter gyms by pulse status
  const filledGyms = useMemo(() => {
    if (!pulse || !gyms) return [];
    const pulseKey = pulse.toLowerCase();
    return gyms.filter(g => g[`${pulseKey}_status`] === 'filled');
  }, [pulse, gyms]);

  const notFilledGyms = useMemo(() => {
    if (!pulse || !gyms) return [];
    const pulseKey = pulse.toLowerCase();
    return gyms.filter(g => g[`${pulseKey}_status`] !== 'filled');
  }, [pulse, gyms]);

  // Sort gyms hierarchically: Manager -> TL -> Gym (ascending)
  const sortGyms = (gymList) => {
    return [...gymList].sort((a, b) => {
      if (a.manager_name !== b.manager_name) {
        return (a.manager_name || '').localeCompare(b.manager_name || '');
      }
      if (a.tl_name !== b.tl_name) {
        return (a.tl_name || '').localeCompare(b.tl_name || '');
      }
      return (a.gym_name || '').localeCompare(b.gym_name || '');
    });
  };

  const handleViewEntry = (gymId) => {
    const date = selectedDate || new Date().toISOString().split('T')[0];
    navigate(`/pulse-entry?gym_id=${gymId}&date=${date}`);
    onClose();
  };

  const handleSendReminder = async (tlId, gymName) => {
    if (!tlId) {
      toast.error('Team Leader ID not available');
      return;
    }

    setSendingReminder(prev => ({ ...prev, [tlId]: true }));
    try {
      await axiosInstance.post('/send-pulse-reminder', {
        tl_id: tlId,
        pulse_name: pulse,
        message_type: 'missing_pulse'
      });
      toast.success(`Reminder sent to Team Leader for ${gymName}`);
    } catch (error) {
      console.error('Failed to send reminder:', error);
      toast.error('Failed to send reminder');
    } finally {
      setSendingReminder(prev => ({ ...prev, [tlId]: false }));
    }
  };

  const renderGymCard = (gym, type) => {
    const pulseKey = pulse?.toLowerCase();
    const status = pulseKey ? gym[`${pulseKey}_status`] : 'unfilled';
    const verified = pulseKey ? gym[`${pulseKey}_verified`] : false;

    let cardBgClass = 'bg-gray-50 border-gray-200';
    let badgeClass = 'bg-gray-50 text-gray-600 border-gray-300';
    let badgeText = 'Missing';

    if (type === 'filled') {
      cardBgClass = 'bg-green-50 border-green-200';
      badgeClass = verified
        ? 'bg-green-100 text-green-700 border-green-300'
        : 'bg-yellow-100 text-yellow-700 border-yellow-300';
      badgeText = verified ? 'Verified' : 'Filled';
    } else if (type === 'not_filled') {
      cardBgClass = 'bg-red-50 border-red-200';
    } else if (type === 'all') {
      // For "All Gyms" tab, determine badge based on actual status
      if (status === 'filled') {
        cardBgClass = 'bg-green-50 border-green-200';
        badgeClass = verified
          ? 'bg-green-100 text-green-700 border-green-300'
          : 'bg-yellow-100 text-yellow-700 border-yellow-300';
        badgeText = verified ? 'Verified' : 'Filled';
      } else {
        cardBgClass = 'bg-red-50 border-red-200';
        badgeClass = 'bg-red-50 text-red-600 border-red-300';
        badgeText = 'Missing';
      }
    }

    return (
      <Card key={gym.gym_id} className={`p-3 ${cardBgClass} border`}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Building2 className="w-4 h-4 text-gray-500 flex-shrink-0" />
              <p className="font-semibold text-sm truncate">{gym.gym_name}</p>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <Users className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">
                TL: {gym.tl_name || 'N/A'} | Manager: {gym.manager_name || 'N/A'}
              </span>
            </div>
            {type === 'all' && (
              <div className="mt-2">
                <Badge variant="outline" className={badgeClass}>
                  {pulse}: {badgeText}
                </Badge>
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2 flex-shrink-0">
            {type === 'filled' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleViewEntry(gym.gym_id)}
                className="flex items-center gap-1 text-xs"
              >
                <Eye className="w-3 h-3" />
                View Entry
              </Button>
            )}
            {type === 'not_filled' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleSendReminder(gym.tl_id, gym.gym_name)}
                disabled={sendingReminder[gym.tl_id]}
                className="flex items-center gap-1 text-xs bg-white hover:bg-red-100"
              >
                <Send className="w-3 h-3" />
                {sendingReminder[gym.tl_id] ? 'Sending...' : 'Remind TL'}
              </Button>
            )}
          </div>
        </div>
      </Card>
    );
  };

  const renderGymList = (gymList, type) => {
    const sorted = sortGyms(gymList);

    if (sorted.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          No gyms in this category for {pulse}.
        </div>
      );
    }

    return (
      <div className="space-y-2 max-h-[400px] overflow-y-auto">
        {sorted.map(gym => renderGymCard(gym, type))}
      </div>
    );
  };

  if (!open || !pulse) return null;

  const filledCount = filledGyms.length;
  const notFilledCount = notFilledGyms.length;
  const totalCount = gyms?.length || 0;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">
            {pulse}: {filledCount} Filled, {notFilledCount} Not Filled ({totalCount} Total)
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="filled" className="w-full mt-4">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="filled" className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-green-500"></span>
              Filled ({filledCount})
            </TabsTrigger>
            <TabsTrigger value="not_filled" className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-red-500"></span>
              Not Filled ({notFilledCount})
            </TabsTrigger>
            <TabsTrigger value="all" className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-gray-400"></span>
              All Gyms ({totalCount})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="filled" className="mt-4">
            {renderGymList(filledGyms, 'filled')}
          </TabsContent>

          <TabsContent value="not_filled" className="mt-4">
            {renderGymList(notFilledGyms, 'not_filled')}
          </TabsContent>

          <TabsContent value="all" className="mt-4">
            {renderGymList(gyms || [], 'all')}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
